import { ReactNode } from 'react';
import { useExamPermission } from '../hooks/useExamPermission';
import { useExamPermissions } from '../hooks/useExamPermissions';

interface PermissionGateProps {
  /** Single permission to check */
  permission?: string;
  /** Multiple permissions */
  permissions?: string[];
  /** Match mode: 'any' (default) or 'all' */
  mode?: 'any' | 'all';
  /** Resource ID for Zanzibar resource-specific checks */
  resourceId?: string;
  /** Content to render when allowed */
  children: ReactNode;
  /** Content to render when denied */
  fallback?: ReactNode;
  /** Content to render while loading */
  loading?: ReactNode;
}

/**
 * Conditionally renders children based on Zanzibar/JWT permissions.
 *
 * Usage:
 *   <PermissionGate permission="exam:create:exams:own">
 *     <CreateExamButton />
 *   </PermissionGate>
 *
 *   <PermissionGate permissions={['exam:grade:attempts:own', 'exam:grade:attempts:all']} mode="any">
 *     <GradingPanel />
 *   </PermissionGate>
 */
export function PermissionGate({
  permission,
  permissions,
  mode = 'any',
  resourceId,
  children,
  fallback = null,
  loading: loadingComponent = null,
}: PermissionGateProps) {
  if (permission && !permissions) {
    return (
      <SingleGate
        permission={permission}
        resourceId={resourceId}
        fallback={fallback}
        loadingComponent={loadingComponent}
      >
        {children}
      </SingleGate>
    );
  }

  if (permissions && permissions.length > 0) {
    return (
      <BatchGate
        permissions={permissions}
        mode={mode}
        fallback={fallback}
        loadingComponent={loadingComponent}
      >
        {children}
      </BatchGate>
    );
  }

  return <>{children}</>;
}

function SingleGate({
  permission,
  resourceId,
  children,
  fallback,
  loadingComponent,
}: {
  permission: string;
  resourceId?: string;
  children: ReactNode;
  fallback: ReactNode;
  loadingComponent: ReactNode;
}) {
  const { allowed, loading } = useExamPermission(permission, resourceId);
  if (loading) return <>{loadingComponent}</>;
  if (!allowed) return <>{fallback}</>;
  return <>{children}</>;
}

function BatchGate({
  permissions,
  mode,
  children,
  fallback,
  loadingComponent,
}: {
  permissions: string[];
  mode: 'any' | 'all';
  children: ReactNode;
  fallback: ReactNode;
  loadingComponent: ReactNode;
}) {
  const { results, loading } = useExamPermissions(permissions);
  if (loading) return <>{loadingComponent}</>;

  const values = Object.values(results);
  const granted =
    mode === 'any'
      ? values.some(Boolean)
      : values.length > 0 && values.every(Boolean);

  if (!granted) return <>{fallback}</>;
  return <>{children}</>;
}
